import { Table as AntdTable, type TableProps as AntdTableProps } from 'antd';
import { useMemo, useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { getGenericFilters, toQueryFormat } from '@/components/table/utils';
import { authStore, settingsStore } from '@/stores';
import type { CollectionStore, RelCollectionStore } from '@/stores/collection';

interface TableProps extends AntdTableProps<any> {
  store?: RelCollectionStore<any, any> | CollectionStore<any, any>;
  fillStore?: (params: any) => Promise<any>;
  checkPermissions?: boolean;
  relId?: number;
  /** When scroll.x is set, show a horizontal scrollbar strip above the table (default: true) */
  showTopHorizontalScrollbar?: boolean;
}

export const Table = observer((props: TableProps) => {
  const { columns, store, fillStore, checkPermissions, relId, showTopHorizontalScrollbar } = props;
  let finalColumns = [...columns];

  const containerRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement | null>(null);
  const [scrollStripWidth, setScrollStripWidth] = useState(0);
  const syncFromStripRef = useRef(false);
  const scrollX =
    props.scroll && typeof (props.scroll as { x?: number }).x !== 'undefined' ? (props.scroll as { x: number }).x : 0;
  const hasHorizontalScroll = scrollX !== 0 && showTopHorizontalScrollbar !== false;

  const genericFilters = useMemo(() => getGenericFilters(columns), [columns]);

  if (checkPermissions) {
    finalColumns = finalColumns.filter(({ permissions }: any) => {
      if (!permissions) {
        return true;
      }

      return authStore.hasPermissions(permissions);
    });
  }

  const onChangeTable = (...args) => {
    const params = toQueryFormat.apply(null, [genericFilters, ...args]);
    const fillStoreArgs = relId ? [relId, params] : [params];
    fillStore ? fillStore.apply(null, fillStoreArgs) : store.forceLoad.apply(store, fillStoreArgs);
  };

  // No store/fillStore → local dataSource; let antd paginate it internally (no server reload to trigger).
  const onChange = props.onChange || (store || fillStore ? onChangeTable : undefined);
  const pagination =
    props.pagination !== undefined
      ? props.pagination
      : {
          total: store?.count,
          showSizeChanger: true,
          onShowSizeChange: (current, size) => {
            settingsStore.changeItemsPerPage(size);
          },
          pageSize: settingsStore.itemsPerPage,
        };

  if (pagination && !pagination.pageSize) {
    pagination.pageSize = settingsStore.itemsPerPage;
  }

  useEffect(() => {
    if (!hasHorizontalScroll || !containerRef.current) return;

    const container = containerRef.current;

    const setup = (): (() => void) | undefined => {
      const content = container.querySelector('.ant-table-content') as HTMLDivElement | null;
      const body = container.querySelector('.ant-table-body') as HTMLDivElement | null;
      const scrollEl = content || body;
      if (!scrollEl) return undefined;

      tableBodyRef.current = scrollEl;

      const updateStripWidth = () => {
        const w = scrollEl.scrollWidth;
        setScrollStripWidth(w);
      };

      updateStripWidth();

      const syncStripFromTable = () => {
        if (syncFromStripRef.current) return;
        const left = scrollEl.scrollLeft;
        if (stripRef.current && stripRef.current.scrollLeft !== left) {
          stripRef.current.scrollLeft = left;
        }
      };

      const onStripScroll = () => {
        if (!stripRef.current) return;
        const left = stripRef.current.scrollLeft;
        syncFromStripRef.current = true;
        scrollEl.scrollLeft = left;
        if (content && body && content !== body) {
          body.scrollLeft = left;
        }
        requestAnimationFrame(() => {
          syncFromStripRef.current = false;
        });
      };

      const stripEl = stripRef.current;
      scrollEl.addEventListener('scroll', syncStripFromTable);
      if (content && body && content !== body) {
        body.addEventListener('scroll', syncStripFromTable);
      }
      stripEl?.addEventListener('scroll', onStripScroll);

      if (stripEl) {
        stripEl.scrollLeft = scrollEl.scrollLeft;
      }

      const resizeObserver = new ResizeObserver(() => updateStripWidth());
      resizeObserver.observe(scrollEl);

      return () => {
        scrollEl.removeEventListener('scroll', syncStripFromTable);
        if (content && body && content !== body) {
          body.removeEventListener('scroll', syncStripFromTable);
        }
        stripEl?.removeEventListener('scroll', onStripScroll);
        resizeObserver.disconnect();
        tableBodyRef.current = null;
      };
    };

    const cleanup: (() => void) | undefined = setup();
    if (cleanup) {
      return cleanup;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    const cleanupRef = { current: undefined as (() => void) | undefined };
    timeoutId = setTimeout(() => {
      cleanupRef.current = setup();
    }, 150);
    return () => {
      clearTimeout(timeoutId);
      cleanupRef.current?.();
    };
  }, [hasHorizontalScroll, columns, props.dataSource]);

  const tableNode = <AntdTable {...props} columns={finalColumns} onChange={onChange} pagination={pagination} />;

  if (hasHorizontalScroll) {
    const stripInnerWidth = scrollStripWidth > 0 ? scrollStripWidth : typeof scrollX === 'number' ? scrollX : 0;
    return (
      <div ref={containerRef} className="table-with-top-scroll">
        <div ref={stripRef} className="table-h-scroll-strip">
          <div className="table-h-scroll-strip-inner" style={{ width: stripInnerWidth }} />
        </div>
        {tableNode}
      </div>
    );
  }

  return tableNode;
});
